/**
 * TimeframeOptionsModal Component
 *
 * Modal for managing user-defined timeframe options.
 * Provides CRUD operations for the timeframeOptions array in BasicSettings.
 * Timeframes are displayed in deterministic sorted order (category priority + interval).
 */

// 1. External dependencies
import React, { useState, useEffect, useCallback, useMemo } from 'react';

// 2. Internal absolute imports
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Select,
  SelectItem,
  NumberInput,
  Alert,
  Divider
} from '@enfomentis/ui';
import { Plus, Pencil, Trash2, Check, X } from 'lucide-react';

// 3. Services & Types
import { basicSettingsService } from '@/services/BasicSettingsService';
import type { TimeframeCategory, TimeframeOption } from '@/types/schema/BasicSettings';

// 4. Utils
import { getTimeframeLabel, sortTimeframeOptions } from '@/utils';
import { generateUUID } from '@/utils/uuid';

/**
 * Props for TimeframeOptionsModal
 */
export interface TimeframeOptionsModalProps {
  /** Whether the modal is open */
  isOpen: boolean;
  /** Callback when modal closes */
  onClose: () => void;
  /** Optional callback when options are changed */
  onOptionsChange?: (options: TimeframeOption[]) => void;
}

/**
 * Category options for the Select dropdown
 */
const CATEGORY_OPTIONS: { key: TimeframeCategory; label: string }[] = [
  { key: 'ticks', label: 'Ticks' },
  { key: 'minutes', label: 'Minutes' },
  { key: 'hours', label: 'Hours' },
  { key: 'days', label: 'Days' },
  { key: 'weeks', label: 'Weeks' },
  { key: 'months', label: 'Months' },
  { key: 'range', label: 'Range' }
];

/**
 * TimeframeOptionsModal Component
 */
export const TimeframeOptionsModal: React.FC<TimeframeOptionsModalProps> = ({
  isOpen,
  onClose,
  onOptionsChange
}) => {
  // Options state
  const [options, setOptions] = useState<TimeframeOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Add form state
  const [addCategory, setAddCategory] = useState<TimeframeCategory>('minutes');
  const [addInterval, setAddInterval] = useState<number>(15);
  const [addError, setAddError] = useState<string | null>(null);

  // Edit state
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editCategory, setEditCategory] = useState<TimeframeCategory>('minutes');
  const [editInterval, setEditInterval] = useState<number>(1);
  const [editError, setEditError] = useState<string | null>(null);

  // Sorted options for display
  const sortedOptions = useMemo(() => sortTimeframeOptions(options), [options]);

  /**
   * Check if a category+interval combination already exists
   * Normalizes interval via Math.floor to mirror service behavior
   */
  const isDuplicate = useCallback(
    (category: TimeframeCategory, interval: number, excludeId?: string): boolean => {
      const normalizedInterval = Math.floor(interval);
      return options.some(
        (opt) =>
          opt.category === category &&
          opt.interval === normalizedInterval &&
          opt.id !== excludeId
      );
    },
    [options]
  );

  /**
   * Load options from settings
   */
  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const settings = await basicSettingsService.getOrCreateDefaultSettings();
      setOptions(settings.timeframeOptions);
    } catch (err) {
      console.error('Failed to load timeframe options:', err);
      setError('Failed to load timeframe options');
    } finally {
      setLoading(false);
    }
  }, []);

  // Load data when modal opens
  useEffect(() => {
    if (isOpen) {
      loadData();
      // Reset form state
      setAddCategory('minutes');
      setAddInterval(15);
      setAddError(null);
      setEditingId(null);
    }
  }, [isOpen, loadData]);

  /**
   * Add a new timeframe option
   */
  const handleAdd = () => {
    setAddError(null);

    // Validate interval
    if (addInterval <= 0) {
      setAddError('Interval must be at least 1');
      return;
    }

    // Check for duplicates
    if (isDuplicate(addCategory, addInterval)) {
      setAddError('This timeframe already exists');
      return;
    }

    const newOption: TimeframeOption = {
      id: generateUUID(),
      category: addCategory,
      interval: Math.floor(addInterval)
    };

    setOptions((prev) => [...prev, newOption]);
    // Reset to defaults
    setAddCategory('minutes');
    setAddInterval(15);
  };

  /**
   * Start editing an option
   */
  const startEditing = (option: TimeframeOption) => {
    setEditingId(option.id);
    setEditCategory(option.category);
    setEditInterval(option.interval);
    setEditError(null); // Clear any previous edit error
  };

  /**
   * Cancel editing
   */
  const cancelEditing = () => {
    setEditingId(null);
    setEditError(null);
  };

  /**
   * Save edited option
   */
  const saveEdit = () => {
    if (!editingId) return;

    // Validate interval
    if (editInterval <= 0) {
      setEditError('Interval must be at least 1');
      return;
    }

    // Check for duplicates (exclude current)
    if (isDuplicate(editCategory, editInterval, editingId)) {
      setEditError('This timeframe already exists');
      return;
    }

    // Update option, preserving original ID
    setOptions((prev) =>
      prev.map((opt) =>
        opt.id === editingId
          ? { ...opt, category: editCategory, interval: Math.floor(editInterval) }
          : opt
      )
    );
    // Clear edit state on successful save
    setEditingId(null);
    setEditError(null);
  };

  /**
   * Handle edit input key press
   */
  const handleEditKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      saveEdit();
    } else if (e.key === 'Escape') {
      cancelEditing();
    }
  };

  /**
   * Delete an option
   */
  const handleDelete = (id: string) => {
    setOptions((prev) => prev.filter((opt) => opt.id !== id));
  };

  /**
   * Save all changes
   */
  const handleSave = async () => {
    try {
      setIsSaving(true);
      setError(null);

      const updatedSettings = await basicSettingsService.updateTimeframeOptions(options);
      // Use returned options to reset local state (service may normalize/filter)
      setOptions(updatedSettings.timeframeOptions);
      onOptionsChange?.(updatedSettings.timeframeOptions);
      onClose();
    } catch (err) {
      console.error('Failed to save timeframe options:', err);
      setError('Failed to save changes');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="md"
      placement="top-center"
      scrollBehavior="inside"
    >
      <ModalContent>
        <ModalHeader>Manage Timeframe Options</ModalHeader>

        <ModalBody className="gap-4">
          {/* Loading state */}
          {loading && (
            <div className="text-center py-4 text-text-secondary">Loading...</div>
          )}

          {/* Error state */}
          {error && (
            <Alert color="danger" variant="flat">
              {error}
            </Alert>
          )}

          {/* Add new timeframe */}
          {!loading && (
            <div className="space-y-2">
              <div className="flex gap-2 items-end">
                <Select
                  label="Category"
                  selectedKeys={[addCategory]}
                  onSelectionChange={(keys) => {
                    const selected = Array.from(keys)[0] as TimeframeCategory;
                    if (selected) setAddCategory(selected);
                  }}
                  size="sm"
                  className="flex-1"
                >
                  {CATEGORY_OPTIONS.map((opt) => (
                    <SelectItem key={opt.key}>{opt.label}</SelectItem>
                  ))}
                </Select>
                <NumberInput
                  label="Interval"
                  value={addInterval}
                  onValueChange={(val) => setAddInterval(val ?? 1)}
                  min={1}
                  size="sm"
                  className="w-24"
                />
                <Button
                  size="sm"
                  color="primary"
                  onPress={handleAdd}
                  startContent={<Plus size={16} />}
                  className="min-h-[44px]"
                >
                  Add
                </Button>
              </div>
              {addError && <p className="text-danger text-sm">{addError}</p>}
            </div>
          )}

          {/* Timeframes list */}
          {!loading && sortedOptions.length > 0 && (
            <>
              <Divider />
              <div className="space-y-2">
                {sortedOptions.map((option) => {
                  const isEditing = editingId === option.id;

                  return (
                    <div
                      key={option.id}
                      className="p-2 rounded-lg bg-default-100"
                    >
                      {isEditing ? (
                        // Edit mode
                        <div className="space-y-2">
                          <div className="flex items-center gap-2">
                            <Select
                              aria-label="Category"
                              selectedKeys={[editCategory]}
                              onSelectionChange={(keys) => {
                                const selected = Array.from(keys)[0] as TimeframeCategory;
                                if (selected) setEditCategory(selected);
                              }}
                              size="sm"
                              className="flex-1"
                            >
                              {CATEGORY_OPTIONS.map((opt) => (
                                <SelectItem key={opt.key}>{opt.label}</SelectItem>
                              ))}
                            </Select>
                            <NumberInput
                              aria-label="Interval"
                              value={editInterval}
                              onValueChange={(val) => setEditInterval(val ?? 1)}
                              min={1}
                              size="sm"
                              className="w-20"
                              onKeyDown={handleEditKeyDown}
                            />
                            <Button
                              size="sm"
                              variant="light"
                              isIconOnly
                              onPress={saveEdit}
                              color="success"
                              className="min-w-[44px] min-h-[44px]"
                            >
                              <Check size={16} />
                            </Button>
                            <Button
                              size="sm"
                              variant="light"
                              isIconOnly
                              onPress={cancelEditing}
                              className="min-w-[44px] min-h-[44px]"
                            >
                              <X size={16} />
                            </Button>
                          </div>
                          {editError && <p className="text-danger text-sm">{editError}</p>}
                        </div>
                      ) : (
                        // View mode
                        <div className="flex items-center gap-2">
                          <span className="flex-1 font-medium">
                            {getTimeframeLabel(option.category, option.interval)}
                          </span>
                          <Button
                            size="sm"
                            variant="light"
                            isIconOnly
                            onPress={() => startEditing(option)}
                            className="min-w-[44px] min-h-[44px]"
                          >
                            <Pencil size={14} />
                          </Button>
                          <Button
                            size="sm"
                            variant="light"
                            isIconOnly
                            onPress={() => handleDelete(option.id)}
                            color="danger"
                            className="min-w-[44px] min-h-[44px]"
                          >
                            <Trash2 size={14} />
                          </Button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </>
          )}

          {/* Empty state */}
          {!loading && options.length === 0 && (
            <div className="text-center py-4 text-text-secondary">
              No timeframes configured. Add your first timeframe above.
            </div>
          )}
        </ModalBody>

        <ModalFooter>
          <Button variant="flat" onPress={onClose} isDisabled={isSaving}>
            Cancel
          </Button>
          <Button
            color="primary"
            onPress={handleSave}
            isLoading={isSaving}
            isDisabled={loading || isSaving}
          >
            Save Changes
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default TimeframeOptionsModal;
